/**
 * Pure library state logic, shared by the client store and its tests.
 *
 * The client keeps a `base` snapshot (what the server last confirmed) plus a
 * list of `pending` operations (optimistic changes whose server action has not
 * finished). What the UI shows is `base` with the pending operations applied,
 * so a failed operation is rolled back simply by dropping it: every collection
 * it touched returns to exactly what it was, even if other operations on other
 * movies finished in the meantime.
 *
 * Operations are idempotent "make it so" changes (add = ensure present,
 * remove = ensure absent), which makes re-applying them on top of a newer
 * server snapshot safe.
 *
 * Library rules (Letterboxd-style):
 *  - marking a movie watched removes it from the watchlist;
 *  - rating a movie marks it watched (and removes it from the watchlist);
 *  - removing a rating keeps the movie watched;
 *  - un-marking a watched movie also removes its rating;
 *  - a watched movie may be added to the watchlist again (rewatch).
 */
import type {
  LibraryRestore,
  LibrarySnapshot,
  MovieId,
  MovieLibraryEntry,
} from "@/types/library";

export const EMPTY_LIBRARY: LibrarySnapshot = Object.freeze({
  watchlist: Object.freeze({}),
  watched: Object.freeze({}),
  ratings: Object.freeze({}),
}) as LibrarySnapshot;

export type LibraryOp =
  | { type: "watchlist/add"; movieId: MovieId; at: string }
  | { type: "watchlist/remove"; movieId: MovieId }
  | { type: "watched/add"; movieId: MovieId; at: string }
  | { type: "watched/remove"; movieId: MovieId }
  | { type: "rating/set"; movieId: MovieId; rating: number; at: string }
  | { type: "rating/remove"; movieId: MovieId }
  | { type: "restore"; movieId: MovieId; entry: LibraryRestore };

type EntryKey = keyof MovieLibraryEntry;

const has = (record: object, key: string) =>
  Object.prototype.hasOwnProperty.call(record, key);

function withValue<T>(
  record: Record<MovieId, T>,
  movieId: MovieId,
  value: T | null,
): Record<MovieId, T> {
  if (value === null) {
    if (!has(record, movieId)) return record;
    const next = { ...record };

    delete next[movieId];

    return next;
  }
  if (has(record, movieId) && record[movieId] === value) return record;

  return { ...record, [movieId]: value };
}

/** One movie's entries (`null` = not in that collection). */
export function getEntry(
  library: LibrarySnapshot,
  movieId: MovieId,
): MovieLibraryEntry {
  return {
    watchlist: has(library.watchlist, movieId)
      ? library.watchlist[movieId]
      : null,
    watched: has(library.watched, movieId) ? library.watched[movieId] : null,
    rating: has(library.ratings, movieId) ? library.ratings[movieId] : null,
  };
}

/**
 * Set some of a movie's entries. Collections that do not change keep their
 * identity, so selectors can cheaply detect what changed.
 */
export function setEntry(
  library: LibrarySnapshot,
  movieId: MovieId,
  patch: LibraryRestore,
): LibrarySnapshot {
  const watchlist =
    patch.watchlist === undefined
      ? library.watchlist
      : withValue(library.watchlist, movieId, patch.watchlist);
  const watched =
    patch.watched === undefined
      ? library.watched
      : withValue(library.watched, movieId, patch.watched);
  const ratings =
    patch.rating === undefined
      ? library.ratings
      : withValue(library.ratings, movieId, patch.rating);

  if (
    watchlist === library.watchlist &&
    watched === library.watched &&
    ratings === library.ratings
  ) {
    return library;
  }

  return { watchlist, watched, ratings };
}

/** Apply one operation (pure). */
export function applyOp(
  library: LibrarySnapshot,
  op: LibraryOp,
): LibrarySnapshot {
  const { movieId } = op;
  const current = getEntry(library, movieId);

  switch (op.type) {
    case "watchlist/add":
      return current.watchlist
        ? library
        : setEntry(library, movieId, { watchlist: { addedAt: op.at } });
    case "watchlist/remove":
      return setEntry(library, movieId, { watchlist: null });
    case "watched/add":
      return setEntry(library, movieId, {
        watched: current.watched ?? { watchedAt: op.at },
        watchlist: null,
      });
    case "watched/remove":
      return setEntry(library, movieId, { watched: null, rating: null });
    case "rating/set":
      return setEntry(library, movieId, {
        rating:
          current.rating?.rating === op.rating
            ? current.rating
            : { rating: op.rating, ratedAt: op.at },
        watched: current.watched ?? { watchedAt: op.at },
        watchlist: null,
      });
    case "rating/remove":
      return setEntry(library, movieId, { rating: null });
    case "restore": {
      const next = setEntry(library, movieId, op.entry);
      const restored = getEntry(next, movieId);

      // A rated movie is always watched.
      if (restored.rating && !restored.watched) {
        return setEntry(next, movieId, {
          watched: { watchedAt: restored.rating.ratedAt },
        });
      }

      return next;
    }
  }
}

/** Collections an operation may change. */
export function touchedBy(op: LibraryOp): EntryKey[] {
  switch (op.type) {
    case "watchlist/add":
    case "watchlist/remove":
      return ["watchlist"];
    case "watched/add":
      return ["watched", "watchlist"];
    case "watched/remove":
      return ["watched", "rating"];
    case "rating/set":
      return ["rating", "watched", "watchlist"];
    case "rating/remove":
      return ["rating"];
    case "restore": {
      const keys = (Object.keys(op.entry) as EntryKey[]).filter(
        (key) => op.entry[key] !== undefined,
      );

      if (op.entry.rating && !keys.includes("watched")) keys.push("watched");

      return keys;
    }
  }
}

/**
 * The state of everything `op` touches, before applying it. Restoring this
 * (see the `restore` operation) undoes the operation precisely.
 */
export function priorEntry(
  library: LibrarySnapshot,
  op: LibraryOp,
): LibraryRestore {
  const entry = getEntry(library, op.movieId);
  const prior: LibraryRestore = {};

  for (const key of touchedBy(op)) {
    (prior as Record<EntryKey, unknown>)[key] = entry[key];
  }

  return prior;
}

/** Every movie id that appears anywhere in the library. */
export function libraryMovieIds(library: LibrarySnapshot): MovieId[] {
  return [
    ...new Set([
      ...Object.keys(library.watchlist),
      ...Object.keys(library.watched),
      ...Object.keys(library.ratings),
    ]),
  ];
}

// ---------------------------------------------------------------------------
// Store state machine
// ---------------------------------------------------------------------------

/** How long confirmed operations are remembered to re-apply over snapshots. */
export const RECENT_OPS_WINDOW_MS = 60_000;

export interface PendingOp {
  seq: number;
  op: LibraryOp;
}

export interface ConfirmedOp extends PendingOp {
  /** Client time (ms) when the server confirmed the operation. */
  confirmedAt: number;
}

/** Why the last sync failed. `network` = the request itself failed. */
export type SyncError =
  "unauthenticated" | "invalid_input" | "failed" | "network";

export interface LibraryCore {
  /** `undefined` while auth is unknown, `null` when signed out. */
  userId: string | null | undefined;
  /** Server snapshot with confirmed operations applied; null until loaded. */
  base: LibrarySnapshot | null;
  /** Server time of the snapshot `base` came from. */
  syncedAt: string | null;
  /** Operations sent to the server but not answered yet (in order). */
  pending: PendingOp[];
  /**
   * Recently confirmed operations. A snapshot requested before an operation
   * was confirmed may not contain it, so these are re-applied on top.
   */
  recent: ConfirmedOp[];
  syncing: boolean;
  syncError: SyncError | null;
}

export const INITIAL_CORE: LibraryCore = Object.freeze({
  userId: undefined,
  base: null,
  syncedAt: null,
  pending: [],
  recent: [],
  syncing: false,
  syncError: null,
}) as LibraryCore;

export type LibraryEvent =
  /** The signed-in user changed (or became known). */
  | {
      type: "user";
      userId: string | null;
      cached: { library: LibrarySnapshot; syncedAt: string } | null;
    }
  | { type: "syncStart" }
  | { type: "syncFailure"; error: SyncError }
  /**
   * A server snapshot: the answer to our own request (`requestedAt` = when it
   * was sent), or one that arrived from another tab or a server-rendered page.
   */
  | {
      type: "snapshot";
      userId: string;
      library: LibrarySnapshot;
      syncedAt: string;
      /** Client time the request was sent, or null when unknown. */
      requestedAt: number | null;
      /** True when this answers our own sync request. */
      fromRequest: boolean;
      now: number;
    }
  | { type: "opStart"; seq: number; op: LibraryOp }
  | { type: "opSuccess"; seq: number; now: number }
  | { type: "opFailure"; seq: number }
  /** An operation another tab completed. */
  | {
      type: "remoteOp";
      userId: string;
      seq: number;
      op: LibraryOp;
      now: number;
    };

function pruneRecent(recent: ConfirmedOp[], now: number): ConfirmedOp[] {
  const fresh = recent.filter(
    (entry) => now - entry.confirmedAt <= RECENT_OPS_WINDOW_MS,
  );

  return fresh.length === recent.length ? recent : fresh;
}

const timeOf = (iso: string | null) => (iso ? Date.parse(iso) : NaN);

export function libraryReducer(
  state: LibraryCore,
  event: LibraryEvent,
): LibraryCore {
  switch (event.type) {
    case "user":
      if (event.userId === state.userId) return state;

      return {
        ...INITIAL_CORE,
        userId: event.userId,
        base: event.userId ? (event.cached?.library ?? null) : null,
        syncedAt: event.userId ? (event.cached?.syncedAt ?? null) : null,
      };

    case "syncStart":
      return state.syncing ? state : { ...state, syncing: true };

    case "syncFailure":
      return { ...state, syncing: false, syncError: event.error };

    case "snapshot": {
      const syncFlags = event.fromRequest
        ? { syncing: false, syncError: null }
        : {};

      if (event.userId !== state.userId) {
        return event.fromRequest ? { ...state, ...syncFlags } : state;
      }

      const isNewer =
        state.base === null ||
        !(timeOf(event.syncedAt) < timeOf(state.syncedAt));

      if (!isNewer)
        return event.fromRequest ? { ...state, ...syncFlags } : state;

      // While nothing was loaded, confirmed operations only live in `recent`
      // (they are never dropped before a snapshot can absorb them).
      const recent =
        state.base === null
          ? state.recent
          : pruneRecent(state.recent, event.now);
      const replay =
        event.requestedAt === null
          ? recent
          : recent.filter((entry) => entry.confirmedAt >= event.requestedAt!);
      const base = replay.reduce(
        (library, entry) => applyOp(library, entry.op),
        event.library,
      );

      return {
        ...state,
        ...syncFlags,
        base,
        syncedAt: event.syncedAt,
        // Operations confirmed before our request was sent are in the snapshot.
        recent: replay,
      };
    }

    case "opStart":
      return {
        ...state,
        pending: [...state.pending, { seq: event.seq, op: event.op }],
      };

    case "opSuccess": {
      const done = state.pending.find((entry) => entry.seq === event.seq);

      if (!done) return state;

      return {
        ...state,
        pending: state.pending.filter((entry) => entry !== done),
        base: state.base ? applyOp(state.base, done.op) : null,
        recent: [
          ...(state.base ? pruneRecent(state.recent, event.now) : state.recent),
          { ...done, confirmedAt: event.now },
        ],
      };
    }

    case "opFailure":
      return state.pending.some((entry) => entry.seq === event.seq)
        ? {
            ...state,
            pending: state.pending.filter((entry) => entry.seq !== event.seq),
          }
        : state;

    case "remoteOp":
      if (event.userId !== state.userId) return state;

      return {
        ...state,
        base: state.base ? applyOp(state.base, event.op) : null,
        recent: [
          ...(state.base ? pruneRecent(state.recent, event.now) : state.recent),
          { seq: event.seq, op: event.op, confirmedAt: event.now },
        ],
      };
  }
}

// ---------------------------------------------------------------------------
// Derived (public) state
// ---------------------------------------------------------------------------

export type LibraryStatus = "loading" | "ready" | "anonymous" | "error";

export interface LibraryState {
  status: LibraryStatus;
  userId: string | null;
  /** What the UI should show: confirmed state plus optimistic changes. */
  library: LibrarySnapshot;
  /** Movies with a change still waiting for the server. */
  pendingIds: ReadonlySet<MovieId>;
  isSyncing: boolean;
  syncError: SyncError | null;
  syncedAt: string | null;
}

export function libraryStatus(core: LibraryCore): LibraryStatus {
  if (core.userId === undefined) return "loading";
  if (core.userId === null) return "anonymous";
  if (core.base) return "ready";

  return core.syncError ? "error" : "loading";
}

/** The library as the UI should show it. */
export function visibleLibrary(core: LibraryCore): LibrarySnapshot {
  if (core.base) {
    return core.pending.reduce(
      (library, entry) => applyOp(library, entry.op),
      core.base,
    );
  }

  return [...core.recent, ...core.pending]
    .sort((a, b) => a.seq - b.seq)
    .reduce((library, entry) => applyOp(library, entry.op), EMPTY_LIBRARY);
}

const EMPTY_IDS: ReadonlySet<MovieId> = new Set();

export const INITIAL_STATE: LibraryState = Object.freeze({
  status: "loading",
  userId: null,
  library: EMPTY_LIBRARY,
  pendingIds: EMPTY_IDS,
  isSyncing: false,
  syncError: null,
  syncedAt: null,
}) as LibraryState;

function sameIds(a: ReadonlySet<MovieId>, b: ReadonlySet<MovieId>) {
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;

  return true;
}

/**
 * Derive the public state, reusing the previous object (and its parts) when
 * nothing observable changed so React subscribers skip needless renders.
 */
export function derivePublicState(
  core: LibraryCore,
  previous: LibraryState,
): LibraryState {
  const library = visibleLibrary(core);
  const pendingIds = core.pending.length
    ? new Set(core.pending.map((entry) => entry.op.movieId))
    : EMPTY_IDS;
  const next: LibraryState = {
    status: libraryStatus(core),
    userId: core.userId ?? null,
    library:
      library.watchlist === previous.library.watchlist &&
      library.watched === previous.library.watched &&
      library.ratings === previous.library.ratings
        ? previous.library
        : library,
    pendingIds: sameIds(pendingIds, previous.pendingIds)
      ? previous.pendingIds
      : pendingIds,
    isSyncing: core.syncing,
    syncError: core.syncError,
    syncedAt: core.syncedAt,
  };

  return (Object.keys(next) as Array<keyof LibraryState>).every(
    (key) => next[key] === previous[key],
  )
    ? previous
    : next;
}

/** One movie's state, as most UI needs it. */
export interface MovieLibraryState {
  inWatchlist: boolean;
  addedAt: string | null;
  watched: boolean;
  watchedAt: string | null;
  rating: number | null;
  ratedAt: string | null;
  /** A change to this movie is waiting for the server. */
  isPending: boolean;
}

export function selectMovieState(
  state: LibraryState,
  movieId: MovieId,
): MovieLibraryState {
  const entry = getEntry(state.library, movieId);

  return {
    inWatchlist: !!entry.watchlist,
    addedAt: entry.watchlist?.addedAt ?? null,
    watched: !!entry.watched,
    watchedAt: entry.watched?.watchedAt ?? null,
    rating: entry.rating?.rating ?? null,
    ratedAt: entry.rating?.ratedAt ?? null,
    isPending: state.pendingIds.has(movieId),
  };
}

export function sameMovieState(
  a: MovieLibraryState,
  b: MovieLibraryState,
): boolean {
  return (Object.keys(a) as Array<keyof MovieLibraryState>).every(
    (key) => a[key] === b[key],
  );
}
